// src/utils/auth.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ref } from 'vue';
import router from '../router';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const REFRESH_BUFFER_MS = 60 * 1000;

export const isLoggedIn = ref(
  !!localStorage.getItem(ACCESS_TOKEN_KEY) || !!localStorage.getItem(REFRESH_TOKEN_KEY)
);

let refreshPromise = null;
let refreshTimeoutId = null;
let authInitialized = false;

function setAuthorizationHeader(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete axios.defaults.headers.common.Authorization;
}

function clearRefreshTimer() {
  if (refreshTimeoutId) {
    window.clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
}

function getTokenExpiryMs(token) {
  if (!token || token.split('.').length !== 3) {
    return null;
  }

  try {
    const { exp } = jwtDecode(token);
    return exp * 1000;
  } catch (error) {
    console.error('Token decoding failed:', error);
    return null;
  }
}

function scheduleTokenRefresh(token) {
  clearRefreshTimer();

  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) {
    return;
  }

  const refreshInMs = Math.max(expiryMs - Date.now() - REFRESH_BUFFER_MS, 0);
  refreshTimeoutId = window.setTimeout(() => {
    refreshAccessToken().catch(() => {});
  }, refreshInMs);
}

function setAuthTokens({ accessToken, refreshToken }) {
  const resolvedAccessToken = accessToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);
  const resolvedRefreshToken = refreshToken ?? localStorage.getItem(REFRESH_TOKEN_KEY);

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  setAuthorizationHeader(resolvedAccessToken);
  isLoggedIn.value = !!resolvedAccessToken || !!resolvedRefreshToken;

  if (resolvedAccessToken) {
    scheduleTokenRefresh(resolvedAccessToken);
  } else {
    clearRefreshTimer();
  }
}

function clearAuthTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  setAuthorizationHeader(null);
  clearRefreshTimer();
  isLoggedIn.value = false;
}

export function login(authData) {
  const accessToken = authData.access || authData.token;
  setAuthTokens({
    accessToken,
    refreshToken: authData.refresh,
  });
}

export function logout() {
  clearAuthTokens();
  if (router.currentRoute.value.name !== 'Login') {
    router.push('/login');
  }
}

function isTokenExpired(token) {
  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) {
    console.warn('Invalid token format detected.');
    return true;
  }
  return Date.now() >= expiryMs;
}

function shouldRefreshSoon(token) {
  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) {
    return true;
  }
  return expiryMs - Date.now() <= REFRESH_BUFFER_MS;
}

export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    logout();
    throw new Error('No refresh token available');
  }

  refreshPromise = axios.post(
    '/api/v1/token/refresh/',
    { refresh: refreshToken },
    { skipAuthRefresh: true }
  )
    .then((response) => {
      setAuthTokens({
        accessToken: response.data.access,
        refreshToken: response.data.refresh,
      });
      return response.data.access;
    })
    .catch((error) => {
      logout();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

function refreshAccessTokenIfNeeded() {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!refreshToken) {
    return Promise.resolve();
  }

  if (!accessToken || isTokenExpired(accessToken) || shouldRefreshSoon(accessToken)) {
    return refreshAccessToken().catch(() => {});
  }

  setAuthTokens({ accessToken, refreshToken });
  return Promise.resolve();
}

export function initializeAuth() {
  setAuthorizationHeader(localStorage.getItem(ACCESS_TOKEN_KEY));

  if (authInitialized) {
    return refreshAccessTokenIfNeeded();
  }

  authInitialized = true;

  window.addEventListener('focus', () => {
    refreshAccessTokenIfNeeded();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshAccessTokenIfNeeded();
    }
  });

  return refreshAccessTokenIfNeeded();
}

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response || !originalRequest) {
      return Promise.reject(error);
    }

    const isRefreshEndpoint = originalRequest.url?.endsWith('/token/refresh/');
    if (error.response.status === 401 && isRefreshEndpoint) {
      logout();
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      error.response.data?.code === 'token_not_valid' &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
